import { useRef, useState } from 'react'

import TextInput from "./TextInput"
import TextInputBorder from "./TextInputBorder"
import ClearInputButton from "./ClearInputButton"
import Submit from "./Submit"
import InputWrapper from "./InputWrapper"
import FormWrapper from "./FormWrapper"

import { sendUrlUpload, checkUpload } from '@templateroller/helpers/dist/api'
import { sendUploadStartEvent, sendUploadFinishEvent } from '@templateroller/helpers/dist/api/dot-events'
import type { TStatus } from '../Uploader'
import { redirectToDraw } from './helpers'
import { DESCRIPTION_UNDER_DOWNLOAD } from './texts'
import Description from './ContentDescription'

type Props = {
    setUploadStatus: (status: TStatus, message: string, progress: number) => void,
}

function validateUrl(url: string) {
    // eslint-disable-next-line no-useless-escape
    return url.match(/^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/)
}

const Url = ({
    setUploadStatus,
}: Props) => {
    const ref = useRef<HTMLInputElement>(null)
    const [ hasError, setError ] = useState(false)

    const successCallback = (docId: number) => redirectToDraw(docId)
    const errorCallback = () => setUploadStatus('error', 'error', 0)

    const sendUrl = () => {
        if (!ref.current) throw Error("divRef is not assigned")

        if (validateUrl(ref.current.value)) {
            setError(false)
            setUploadStatus('uploading', '', 0)
            submitUpload()
        } else {
            setError(true)
        }
    }

    const ClearInput = () => {
        if (!ref.current) throw Error("divRef is not assigned")

        ref.current.value = '';
        setError(false)
        ref.current.focus()
    }

    const submitUpload = () => {
        if (!ref.current) throw Error("divRef is not assigned")

        const url = ref.current.value
        sendUploadStartEvent('url').then(
            () => sendUrlUpload(
                'url',
                url,
                setUploadStatus,
            )
        ).then(async (data: any) => {
            if (data.status === "success") {
                let check, errorCount = 0
                while (check === undefined || check.status === 'waiting' || check.status === 'pending') {

                    check = await checkUpload(data.doc_id)
                    if (check.status === 'pending') {
                        setUploadStatus('converting', '', (check.cur_page * 100) / check.total_pages)
                    } else if (check.status === 'waiting') {
                        setUploadStatus('converting', '', 0)
                    } else if (check.status === 'success') {
                        setUploadStatus('redirecting', String(check.doc_id), 0)
                    } else if (check.status === 'error') {
                        errorCount++
                        setUploadStatus(check.status, '', 0)
                    } else {
                        setUploadStatus(check.status, '', 0)
                    } 

                    await new Promise(r => setTimeout(r, 3000));
                }

                if (errorCount === 0) {
                    await sendUploadFinishEvent('url')
                    if (process.env.REACT_APP_ENV === 'production') successCallback(data.doc_id)
                }
            } else {
                errorCallback()
            }
        }).catch(errorCallback)
    }

    return <>
        <FormWrapper>
            <InputWrapper>
                <TextInput
                    ref={ref}
                    type="text"
                    placeholder="https://"
                    error={hasError}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            sendUrl()
                        }
                    }}
                />
                <TextInputBorder
                    error={hasError}
                ></TextInputBorder>
                <ClearInputButton
                    error={hasError}
                    onClick={() => ClearInput()}
                ></ClearInputButton>
            </InputWrapper>
            <Submit
                onClick={() => sendUrl()}
            >
                Upload
            </Submit>
        </FormWrapper>
        <Description
            className="form-description"
            error={hasError}
        >
            {DESCRIPTION_UNDER_DOWNLOAD}
        </Description>
    </>
}

export default Url